import {
  Either,
  Maybe,
  Validation,
} from 'monet';
import LeftResponse from '../LeftResponse';

/**
 * @description Категория услуги.
 * @param {number} id   Идентификатор.
 * @param {string} name Название.
 * @param {number} parentCategoryId   Идентификатор родительской категории.
 * @param {number} order  Порядок отрисовки.
 * @param {number|null} priceId  Id прайса.
 * @constructor
 */
function ServiceCategory(
  id,
  name,
  parentCategoryId,
  order,
  priceId = null,
) {
  this.id = id;
  this.name = name;
  this.parentCategoryId = parentCategoryId;
  this.order = order;
  this.priceId = priceId;
}

/**
 * @description Запрос на увелечения цены на услуги.
 * @param serviceIds {array<number>}
 * @param percent    {int}
 * @constructor
 */
export function IncreaseServicePricesRequest(
  serviceIds,
  percent,
) {
  this.toQuery = () => ({
    service_ids: serviceIds,
    increase_percent: percent,
  });
}

/**
 * @description Прайс категорий
 *
 * @param id {number}                                Id
 * @param name {string}                              Имя Прайса
 * @param serviceCategories {Array<ServiceCategory>} Категории услуг
 * @constructor
 */
export function ServicePriceList(
  id,
  name,
  serviceCategories,
) {
  this.id = id;
  this.name = name;
  this.serviceCategories = serviceCategories;
  /**
   * @description Сортировать категории.
   * @return void
   */
  this.sortCategories = () => {
    const subcategories = this.serviceCategories
      .filter((category) => category.parentCategoryId);
    const categories = this
      .serviceCategories
      .filter((category) => !category.parentCategoryId);
    subcategories.reverse()
      .forEach((subcategory) => {
        const index = categories.map((e) => e.id)
          .indexOf(subcategory.parentCategoryId);
        categories.splice(index + 1, 0, subcategory);
      });
    this.serviceCategories = categories;
  };
  this.sortCategories();
}
/**
 * @description Услуга.
 * @param {number}        categoryId
 * @param {(string|null)} code
 * @param {string}        color
 * @param {string}        duration
 * @param {number}        id
 * @param {boolean}       isFavorite
 * @param {boolean}       isHidden
 * @param {string}        itemType
 * @param {string}        name
 * @param {number}        price
 * @param {?number}       taxCode
 * @param {string}        units
 * @param {?number}       clinicCode
 * @param {?number}       priceListId
 * @param {number}        order
 * @param {string}        categoryName
 * @param {string}        priceListName
 * @constructor
 */
function Service(
  categoryId,
  code,
  color,
  duration,
  id,
  isFavorite,
  isHidden,
  itemType,
  name,
  price,
  taxCode,
  units,
  clinicCode,
  priceListId,
  order,
  categoryName = '',
  priceListName = '',
) {
  this.categoryId = categoryId;
  this.code = code ?? '';
  this.color = color;
  this.duration = duration;
  this.id = id;
  this.isFavorite = isFavorite;
  this.isHidden = isHidden;
  this.itemType = itemType;
  this.name = name;
  this.price = price;
  this.taxCode = taxCode;
  this.units = units;
  this.clinicCode = clinicCode ?? '';
  this.isService = itemType === 'service';
  this.priceListId = priceListId === 0 ? null : priceListId;
  this.order = order ?? 0;
  this.categoryName = categoryName;
  this.priceListName = priceListName;

  /**
   * @description Создать копию с новыми полями.
   * @param {{duration,id,name,price,taxCode,code,clinicCode}} newProps Новые поля.
   * @returns {Service}
   */
  this.copyWith = (newProps = {}) => new this.constructor(
    this.categoryId,
    newProps.code ?? this.code,
    this.color,
    newProps.duration ?? this.duration,
    this.id,
    this.isFavorite,
    this.isHidden,
    this.itemType,
    newProps.name ?? this.name,
    newProps.price ?? this.price,
    newProps.taxCode === undefined ? this.taxCode : newProps.taxCode,
    this.units,
    newProps.clinicCode ?? this.clinicCode,
    this.priceListId,
    this.order,
    this.categoryName,
    this.priceListName,
  );

  /**
   * @description Создать копию без идентификатора.
   * @returns {Service}
   */
  this.copyWithoutIdentity = () => new this.constructor(
    this.categoryId,
    this.color,
    this.duration,
    null,
    this.isFavorite,
    this.isHidden,
    this.itemType,
    this.name,
    this.price,
    this.taxCode,
    this.units,
    null,
  );
}

/**
 * Предмет услуги
 * @param {number} id       Идентификатор.
 * @param {string} name     Название.
 */
export function ServiceItem(id, name) {
  this.title = name;
  this.value = id;
  this.text = name;
}

/**
 * @description Запрос на создание услуги.
 * @param {any} props Поля услуги.
 * @constructor
 */
export function CreateServiceRequest(props) {
  /**
   * @description Преобразовать к запросу.
   * @returns {Service}
   */
  this.toService = (serviceId) => new Service(
    props.categoryId,
    props.code || null,
    props.color,
    props.duration,
    serviceId,
    false,
    false,
    props.itemType,
    props.name,
    props.price,
    props.taxCode,
    props.units,
    props.clinicCode,
    props.priceListId,
    props.order,
  );

  /**
   * @description Преобразовать к запросу.
   * @returns {any}
   */
  this.toQuery = () => ({
    category_id: props.categoryId,
    color: props.color,
    item_type: props.itemType,
    name: props.name,
    price: parseFloat(props.price) * 100,
    tax_code: props.taxCode,
    units: props.units,
    order: props.order,
    clinic_code: props.clinicCode,
    code: props.code,
    price_list_id: props.priceListId,
  });
}

/**
 * @description Запрос на редактирование услуг.
 * @param {number}         id         Идентификатор услуги.
 * @param {Service|Object} props      Поля услуги.
 * @param {boolean}        fromForm   С формы редактирования.
 * @constructor
 */
export function EditServiceRequest(
  id,
  props,
  fromForm = false,
) {
  /**
   * @description Отображать ошибки запроса в уведомлении.
   * @returns {boolean}
   */
  this.showViolationOnNotifier = () => !fromForm;

  /**
   * @description Получить идентификатор услуги.
   * @returns {number}
   */
  this.getServiceId = () => id;

  /**
   * @description Преобразовать к запросу.
   * @returns {Service}
   */
  this.toService = () => new Service(
    props.categoryId,
    props.code || null,
    props.color,
    props.duration,
    id,
    props.isFavorite,
    props.isHidden,
    props.itemType,
    props.name,
    props.price,
    props.taxCode,
    props.units,
    props.clinicCode,
    props.priceListId,
    props.order,
    props.categoryName,
    props.priceListName,
  );

  /**
   * @description Преобразовать к запросу.
   * @returns {any}
   */
  this.toQuery = () => ({
    category_id: props.categoryId,
    color: props.color,
    duration: '',
    item_type: props.itemType,
    name: props.name,
    price: parseFloat(props.price) * 100,
    tax_code: props.taxCode,
    units: props.units,
    clinic_code: props.clinicCode,
    code: props.code,
    price_list_id: props.priceListId,
  });
}

/**
 * @description Шлюз услуг.
 * @param {Axios} axios
 * @constructor
 */
export default function ServiceGateway(axios) {
  /**
   * @description Добавить услугу в список избранного.
   * @param {number} id Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.addToFavoriteList = (id) => axios
    .put(`/api/services/${id}/favorite`)
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );

  /**
   * @description Удалить услугу из списка избранного.
   * @param {number} id Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.removeFromFavoriteList = (id) => axios
    .delete(`/api/services/${id}/favorite`)
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );

  /**
   * @description Добавить услугу в список скрытых.
   * @param {number} id Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.addToHiddenList = (id) => axios
    .put(`/api/services/${id}/hidden`)
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );

  /**
   * @description Удалить услугу из списка скрытых.
   * @param {number} id Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.removeFromHiddenList = (id) => axios
    .delete(`/api/services/${id}/hidden`)
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          return Either.left(title);
        }
        throw error;
      },
    );

  /**
   * @description Получить все услуги для просмотра.
   * @param {string} match                      Совпадение.
   * @param {number} servicePriceListId         Id прайс-листа.
   * @param {boolean} byDefaultServicePriceList Id прайс-листа.
   * @returns {Promise<Either>}
   */
  this.allForView = async (
    match = '',
    servicePriceListId = null,
    byDefaultServicePriceList = false,
  ) => {
    const params = {
      service_price_list_id: servicePriceListId,
      by_default_service_price_list: byDefaultServicePriceList,
    };
    if (match) {
      params.match = match;
    }
    try {
      const response = await axios.get('/api/services', { params });
      return Either.right((response.data.data || []).map((item) => ({
        duration: item.duration,
        price: item.price,
        title: item.name,
        value: item.id,
        units: item.units,
        clinic_code: item.clinic_code,
        categoryId: item.category_id,
        categoryName: item.category_name,
        priceName: item.service_price_list_name,
      })));
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        const { causes, title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    }
  };

  /**
   * @description Получить избранные услуги для просмотра.
   * @returns {Promise<Maybe>}
   */
  this.allWithFavoritesForView = () => axios
    .get('/api/favorite-services')
    .then(
      async (response) => {
        const mapService = (item) => new ServiceItem(
          item.id,
          item.name,
        );
        const all = response.data.data.map(mapService);
        const favorites = response.data.favorites.map(mapService);
        return Maybe.some({ all, favorites });
      },
    );

  /**
   * @description Получить единицы измерения.
   * @return {Promise<any[]>}
   */
  this.getDefaultUnits = async () => Promise.resolve(
    [
      {
        value: 'за 1 зуб',
        title: 'за 1 зуб',
      },
      {
        value: 'за 1 челюсть',
        title: 'за 1 челюсть',
      },
      {
        value: 'за обе челюсти',
        title: 'за обе челюсти',
      },
      {
        value: '1 шт.',
        title: '1 шт.',
      },
    ],
  );

  /**
   * @description Получить список прайс-листов для просмотра.
   *
   * @returns {Promise<Maybe>}
   */
  this.getServicePriceListsForView = () => axios
    .get('/api/services/price-lists')
    .then(
      async (response) => {
        const prices = response.data.data.service_price_lists_list.map(
          (item) => {
            const categories = item
              .service_categories
              .map(
                (category) => new ServiceCategory(
                  category.id,
                  category.name,
                  category.parent_category_id,
                  category.order,
                  category.price_id,
                ),
              );
            return new ServicePriceList(item.id, item.name, categories);
          },
        );
        const onBoardingStatus = response.data.on_boarding_status;
        return Maybe.fromEmpty({
          actions: response.data.actions,
          prices,
          onBoardingStatus,
          columns: response.data.columns,
        });
      },
    );
  /**
   * @description Получить категории с услугами по поисковому запросу
   * @param {string} query строка запроса по имени услуги
   * @return {Promise}
   */
  this.searchWithCategories = (query) => axios
    .get('/api/search/services', { params: { query } })
    .then((response) => {
      if (response.data.title) {
        return response.data;
      }
      const categories = response.data.data;
      return categories.map((category) => {
        const services = category.services.map((service) => new Service(
          service.category_id,
          service.code,
          service.color,
          service.duration,
          service.id,
          service.is_favorite,
          service.is_hidden,
          service.item_type,
          service.name,
          service.price,
          service.tax_code,
          service.units,
          service.clinic_code,
          service.price_list_id,
          service.order,
          service.category_name,
          service.price_list_name,
        ));
        const formatCategory = category;
        formatCategory.services = services;
        return formatCategory;
      });
    });

  /**
   * @description Получить список услуг для просмотра.
   * @param {number} categoryId Идентификатор категории.
   * @returns {Promise<Service>}
   */
  this.getServiceListForView = (categoryId) => axios
    .get(`/api/services/categories/${categoryId}`)
    .then(async (response) => (response.data || []).map(
      (item) => new Service(
        item.category_id,
        item.code,
        item.color,
        item.duration,
        item.id,
        item.is_favorite,
        item.is_hidden,
        item.item_type,
        item.name,
        item.price,
        item.tax_code,
        item.units,
        item.clinic_code,
        item.price_list_id,
        item.order,
      ),
    ));

  /**
   * @description Отправить запрос на создание услуги.
   * @param {CreateServiceRequest} request
   * @returns {Promise<Either>}
   */
  this.sendCreateService = (request) => axios
    .post('/api/services', request.toQuery())
    .then(
      async (response) => Either.right({
        message: response.data.message,
        service: request.toService(response.data.service_id),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на удаление категории услуги.
   * @param {number} categoryId Идентификатор категории.
   * @returns {Promise<Validation>}
   */
  this.sendDeleteServiceCategory = (categoryId) => axios
    .delete(`/api/services/categories/${categoryId}`)
    .then(
      async (response) => Validation.Success({
        message: response.data.message,
        categoryId,
      }),
      async (error) => {
        if (error.isAxiosError) {
          return Validation.Fail(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на удаление услуги.
   * @param {any} serviceId
   * @returns {Promise<Either>}
   */
  this.sendDeleteService = (serviceId) => axios
    .delete(`/api/services/${serviceId}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование категории.
   * @param {number} categoryId Идентификатор категории.
   * @param {any}    request    Запрос.
   * @returns {Promise<Either>}
   */
  this.sendEditCategory = (categoryId, request) => axios
    .put(`/api/services/categories/${categoryId}`, request)
    .then(
      async (response) => Either.right({
        category: new ServiceCategory(categoryId, request.name, request.parentCategoryId),
        message: response.data.message,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на редактирование нескольких категорий.
   * @param {any}    request    Запрос.
   * @returns {Promise<Either>}
   */
  this.sendEditCategories = async (request) => axios
    .put('/api/services/categories/', request)
    .then(
      async (response) => Either.right({
        message: response.data.message,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование услуги.
   * @param {EditServiceRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.sendEditService = (request) => axios
    .put(`/api/services/${request.getServiceId()}`, request.toQuery())
    .then(
      async (response) => Either.right({
        message: response.data.message,
        onBoardingStatus: response.data.on_boarding_status,
        service: request.toService(),
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование услуг.
   * @param {Object} request Запрос.
   * @returns {Promise<Either>}
   */
  this.sendEditServices = (request) => axios
    .put('/api/services', request)
    .then(
      async (response) => Either.right({
        message: response.data.message,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на создание категории.
   * @param {any} request
   * @returns {Promise<Either>}
   */
  this.sendCreateCategory = (request) => axios
    .post('/api/services/categories', request)
    .then(
      async (response) => Either.right({
        category: new ServiceCategory(
          response.data.category_id,
          request.name,
          request.parentCategoryId,
        ),
        message: response.data.message,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Установить код услуги для всех услуг раздела.
   * @param {number}  categoryId Идентификатор категории.
   * @param {?string} taxCode    Код услуги для налоговой.
   * @returns {Promise<Either>}
   */
  this.setTaxCodeForAllServicesInCategory = (categoryId, taxCode) => axios
    .put(
      `/api/services/categories/${categoryId}/tax-code`,
      { tax_code: taxCode },
    )
    .then(
      async (response) => Either.right(response.data.message),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Скачать прайс в виде таблицы.
   * @returns {Promise<Validation>}
   */
  this.downloadPriceList = async () => {
    try {
      const response = await axios.get('/api/services/excel', { responseType: 'blob' });
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Добавить прайс.
   *
   * @param {object} request
   * @return {Promise<Validation>}
   */
  this.addServicePriceList = (request) => axios
    .post('/api/services/categories/price-list', request)
    .then(
      async (response) => {
        const { message } = response.data;
        return Either.right(message);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить прайс-листы.
   * @returns {*}
   */
  this.getPriceLists = () => axios
    .get('/api/services/price-list')
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Изменить прайс-лист.
   *
   * @param request
   *
   * @returns {Promise<Validation>}
   */
  this.editServicePriceList = (request) => axios.put(
    `api/service-price-list/${request.id}`,
    {
      name: request.name,
    },
  ).then(
    async (response) => Either.right(response.data),
    async (error) => {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        const causes = error.response.data.causes || [];
        const { title } = error.response.data;
        const leftResponse = new LeftResponse(causes, title);
        return Either.left(leftResponse);
      }
      throw error;
    },
  );

  /**
   * @description Удалить прайс-лист.
   *
   * @param id
   *
   * @returns {Promise<Validation>}
   */
  this.deleteServicePriceList = (id) => axios
    .delete(
      `api/service-price-list/${id}`,
    ).then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Увеличить цены на услуги.
   * @param request {IncreaseServicePricesRequest}
   * @returns {void}
   */
  this.increaseServicePrices = (request) => axios
    .put('api/services/increase-prices', request.toQuery())
    .then(
      async (response) => Either.right({
        message: response.data.message,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
